<script setup lang="ts">
import { ShopCore } from "@/abilities/shop/types/shop-core";
import { getUiLanguage } from "@/shared/services/providers/language-provider";
import { useDocumentVisibility } from "@vueuse/core";
import { computed, watch } from "vue";
import { useShopStatusStore } from "../../store/shop-status.store";

// props
const props = defineProps<{
    infoItem: ShopCore.WidgetItem | null;
    quantitySelected: number;
    moreSelectedThanAvailable: boolean;
}>();

const store = useShopStatusStore();

const isGenericShop = computed(() => store.shopType === ShopCore.Type.GENERIC);
const isDemoMode = computed(() => isGenericShop.value && store.isGenericShopDemo);

// An admin can save a new shop configuration while this portal tab stays open.
// The backend uses the new configuration from its next request on.
// This widget therefore reads the configuration again whenever the user returns
// to this browser tab. Otherwise the "Example data (demo mode)" notice stays wrong
// until the user reloads the page.
const visibility = useDocumentVisibility();
watch(visibility, (state) => {
    if (state === "visible" && isGenericShop.value) {
        store.refreshGenericShopDemo();
    }
});

const stockQuantity = computed(() => {
    return props.infoItem?.stock.quantity ?? 0;
});
</script>
<template>
    <!-- "Example data (demo mode)" notice: shown while the generic shop shows example data. This widget shows it in addition to the status message below, not instead of it. -->
    <div v-if="isDemoMode" class="row no-wrap q-mb-md items-center" data-qs="shop-widget-demo-mode">
        <div class="q-py-sm" style="width: 28px; max-width: 28px">
            <i class="fa-regular fa-circle-info fa-2x" style="color: var(--q-info)"></i>
        </div>
        <div class="q-pl-md">{{ $t("shop.Example data (demo mode)") }}</div>
    </div>
    <!-- Shop offline status -->
    <div v-if="store.isShopOnline === false" class="row no-wrap q-mb-md">
        <div class="q-py-sm" style="width: 28px; max-width: 28px">
            <i class="fa-regular fa-circle-info fa-2x" style="color: var(--q-negative)"></i>
        </div>
        <div class="q-pl-md">
            {{ $t("shop.The store is currently unavailable due to maintenance work or errors") }}
        </div>
    </div>
    <!-- Article not found status -->
    <div v-else-if="infoItem === null" class="row no-wrap q-mb-md items-center">
        <div class="q-py-sm" style="width: 28px; max-width: 28px">
            <i class="fa-regular fa-circle-info fa-2x" style="color: var(--q-negative)"></i>
        </div>
        <div class="q-pl-md">{{ $t("shop.Article not found") }}</div>
    </div>
    <!-- Amount availability warning -->
    <div v-else-if="moreSelectedThanAvailable" class="row no-wrap q-mb-md">
        <div><i class="fa-regular fa-circle-info" style="color: var(--q-warning)"></i></div>
        <div v-if="stockQuantity && stockQuantity > 0" class="q-pl-sm">
            {{
                $t("shop.{additionalAmount} more orderable with unknown delivery time", {
                    additionalAmount: (quantitySelected - stockQuantity).toLocaleString(getUiLanguage(), {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                    }),
                })
            }}
        </div>
        <div v-else class="q-pl-md">
            {{ $t("shop.Orderable with unknown delivery time") }}
        </div>
    </div>
</template>
